import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

// Types for offline forms functionality
export interface CachedForm {
  id: string;
  template: any; // Full form structure
  validationRules: any[];
  assets: CachedAsset[];
  version: string;
  cachedAt: Date;
  lastUsed: Date;
  size: number; // Storage size in bytes
}

export interface CachedAsset {
  url: string;
  type: 'css' | 'image' | 'font' | 'script';
  data: string; // base64 or text content
  size: number;
}

export interface PendingSubmission {
  id: string;
  formId: string;
  formData: Record<string, any>;
  fileUploads: FileUploadQueue[];
  status: 'pending' | 'syncing' | 'completed' | 'failed' | 'conflict';
  attempts: number;
  createdAt: Date;
  lastAttemptAt: Date | null;
  conflictData?: ConflictInfo;
  priority: 'low' | 'normal' | 'high';
}

export interface FileUploadQueue {
  id: string;
  submissionId: string;
  fieldId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadProgress: number;
  tusUploadUrl?: string;
  status: 'queued' | 'uploading' | 'paused' | 'completed' | 'failed';
  isLargeFile: boolean; // true if >1MB
  base64Data?: string; // for small files only
}

export interface ConflictInfo {
  fieldId: string;
  offlineValue: any;
  serverValue: any;
  conflictType: 'different_values' | 'critical_field' | 'timestamp_conflict';
  resolutionRequired: boolean;
}

export interface ConnectionStatus {
  isOnline: boolean;
  quality: 'good' | 'poor' | 'offline';
  lastOnlineAt: Date | null;
  offlineDuration: number;
  networkType?: 'wifi' | 'cellular' | 'unknown';
}

export interface CacheMetrics {
  totalSize: number;
  formCount: number;
  hitRate: number;
  lastUpdate: Date | null;
}

export interface SyncMetrics {
  pendingCount: number;
  successRate: number;
  avgSyncTime: number;
  lastSyncAt: Date | null;
  failureReasons: string[];
}

export interface OfflineFormsState {
  // Connection state
  connection: ConnectionStatus;
  
  // Cache state
  cachedForms: Map<string, CachedForm>;
  cacheMetrics: CacheMetrics;
  
  // Sync queue state
  pendingSubmissions: PendingSubmission[];
  syncMetrics: SyncMetrics;
  syncStatus: 'idle' | 'syncing' | 'error';
  syncProgress: number;
  
  // File upload state
  fileQueue: FileUploadQueue[];
  uploadProgress: Record<string, number>; // fileId -> progress
  
  // UI state
  showOfflineWarning: boolean;
  lastNotificationId: string | null;
}

export interface OfflineFormsActions {
  // Connection actions
  updateConnectionStatus: (status: Partial<ConnectionStatus>) => void;
  
  // Cache actions
  addCachedForm: (form: CachedForm) => void;
  removeCachedForm: (formId: string) => void;
  updateFormUsage: (formId: string) => void;
  getCachedForm: (formId: string) => CachedForm | undefined;
  
  // Submission queue actions
  addPendingSubmission: (submission: Omit<PendingSubmission, 'id' | 'createdAt'>) => string;
  updateSubmissionStatus: (id: string, status: PendingSubmission['status'], conflictData?: ConflictInfo) => void;
  removeSubmission: (id: string) => void;
  incrementSubmissionAttempts: (id: string) => void;
  
  // File upload actions
  addFileToQueue: (file: Omit<FileUploadQueue, 'id'>) => string;
  updateFileProgress: (fileId: string, progress: number) => void;
  updateFileStatus: (fileId: string, status: FileUploadQueue['status']) => void;
  removeFileFromQueue: (fileId: string) => void;
  
  // Sync actions
  updateSyncStatus: (status: OfflineFormsState['syncStatus']) => void;
  updateSyncProgress: (progress: number) => void;
  updateSyncMetrics: (metrics: Partial<SyncMetrics>) => void;
  
  // UI actions
  setShowOfflineWarning: (show: boolean) => void;
  setLastNotificationId: (id: string) => void;
  
  // Utility actions
  cleanupOldData: () => void;
  getCacheStats: () => { totalSize: number; itemCount: number };
  getQueueStats: () => { pendingCount: number; failedCount: number };
}

export type OfflineFormsStore = OfflineFormsState & OfflineFormsActions;

// Helper function to generate unique IDs
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Helper function to calculate file size threshold
const isLargeFile = (size: number) => size > 1024 * 1024; // 1MB

export const useOfflineFormsStore = create<OfflineFormsStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        connection: {
          isOnline: navigator.onLine,
          quality: 'good',
          lastOnlineAt: navigator.onLine ? new Date() : null,
          offlineDuration: 0,
          networkType: 'unknown'
        },
        
        cachedForms: new Map<string, CachedForm>(),
        cacheMetrics: {
          totalSize: 0,
          formCount: 0,
          hitRate: 0,
          lastUpdate: null
        },
        
        pendingSubmissions: [],
        syncMetrics: {
          pendingCount: 0,
          successRate: 0,
          avgSyncTime: 0,
          lastSyncAt: null,
          failureReasons: []
        },
        syncStatus: 'idle',
        syncProgress: 0,
        
        fileQueue: [],
        uploadProgress: {},
        
        showOfflineWarning: false,
        lastNotificationId: null,

        // Connection actions
        updateConnectionStatus: (status) => {
          set((state) => ({
            connection: { ...state.connection, ...status }
          }));
        },

        // Cache actions
        addCachedForm: (form) => {
          set((state) => {
            const newCachedForms = new Map(state.cachedForms);
            newCachedForms.set(form.id, form);
            
            return {
              cachedForms: newCachedForms,
              cacheMetrics: {
                ...state.cacheMetrics,
                formCount: newCachedForms.size,
                totalSize: Array.from(newCachedForms.values()).reduce((sum, f) => sum + f.size, 0),
                lastUpdate: new Date()
              }
            };
          });
        },

        removeCachedForm: (formId) => {
          set((state) => {
            const newCachedForms = new Map(state.cachedForms);
            newCachedForms.delete(formId);
            
            return {
              cachedForms: newCachedForms,
              cacheMetrics: {
                ...state.cacheMetrics,
                formCount: newCachedForms.size,
                totalSize: Array.from(newCachedForms.values()).reduce((sum, f) => sum + f.size, 0)
              }
            };
          });
        },

        updateFormUsage: (formId) => {
          set((state) => {
            const cachedForm = state.cachedForms.get(formId);
            if (!cachedForm) return state;
            
            const newCachedForms = new Map(state.cachedForms);
            newCachedForms.set(formId, {
              ...cachedForm,
              lastUsed: new Date()
            });
            
            return { cachedForms: newCachedForms };
          });
        },

        getCachedForm: (formId) => {
          const state = get();
          return state.cachedForms.get(formId);
        },

        // Submission queue actions
        addPendingSubmission: (submission) => {
          const id = generateId();
          const newSubmission: PendingSubmission = {
            ...submission,
            id,
            createdAt: new Date()
          };
          
          set((state) => ({
            pendingSubmissions: [...state.pendingSubmissions, newSubmission],
            syncMetrics: {
              ...state.syncMetrics,
              pendingCount: state.pendingSubmissions.length + 1
            }
          }));
          
          return id;
        },

        updateSubmissionStatus: (id, status, conflictData) => {
          set((state) => ({
            pendingSubmissions: state.pendingSubmissions.map(sub =>
              sub.id === id 
                ? { 
                    ...sub, 
                    status, 
                    conflictData,
                    lastAttemptAt: new Date() 
                  }
                : sub
            )
          }));
        },

        removeSubmission: (id) => {
          set((state) => ({
            pendingSubmissions: state.pendingSubmissions.filter(sub => sub.id !== id),
            syncMetrics: {
              ...state.syncMetrics,
              pendingCount: Math.max(0, state.syncMetrics.pendingCount - 1)
            }
          }));
        },

        incrementSubmissionAttempts: (id) => {
          set((state) => ({
            pendingSubmissions: state.pendingSubmissions.map(sub =>
              sub.id === id 
                ? { ...sub, attempts: sub.attempts + 1, lastAttemptAt: new Date() }
                : sub
            )
          }));
        },

        // File upload actions
        addFileToQueue: (file) => {
          const id = generateId();
          const newFile: FileUploadQueue = { ...file, id };
          
          set((state) => ({
            fileQueue: [...state.fileQueue, newFile],
            uploadProgress: { ...state.uploadProgress, [id]: 0 }
          }));
          
          return id;
        },

        updateFileProgress: (fileId, progress) => {
          set((state) => ({
            uploadProgress: { ...state.uploadProgress, [fileId]: progress }
          }));
        },

        updateFileStatus: (fileId, status) => {
          set((state) => ({
            fileQueue: state.fileQueue.map(file =>
              file.id === fileId ? { ...file, status } : file
            )
          }));
        },

        removeFileFromQueue: (fileId) => {
          set((state) => {
            const newUploadProgress = { ...state.uploadProgress };
            delete newUploadProgress[fileId];
            
            return {
              fileQueue: state.fileQueue.filter(file => file.id !== fileId),
              uploadProgress: newUploadProgress
            };
          });
        },

        // Sync actions
        updateSyncStatus: (status) => {
          set({ syncStatus: status });
        },

        updateSyncProgress: (progress) => {
          set({ syncProgress: progress });
        },

        updateSyncMetrics: (metrics) => {
          set((state) => ({
            syncMetrics: { ...state.syncMetrics, ...metrics }
          }));
        },

        // UI actions
        setShowOfflineWarning: (show) => {
          set({ showOfflineWarning: show });
        },

        setLastNotificationId: (id) => {
          set({ lastNotificationId: id });
        },

        // Utility actions
        cleanupOldData: () => {
          const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
          const cutoffDate = new Date(Date.now() - THIRTY_DAYS);
          
          set((state) => ({
            pendingSubmissions: state.pendingSubmissions.filter(sub => 
              sub.createdAt > cutoffDate || sub.status !== 'completed'
            ),
            fileQueue: state.fileQueue.filter(file =>
              file.status !== 'completed' // Keep failed/pending files
            )
          }));
        },

        getCacheStats: () => {
          const state = get();
          const totalSize = Array.from(state.cachedForms.values()).reduce((sum, form) => sum + form.size, 0);
          return {
            totalSize,
            itemCount: state.cachedForms.size
          };
        },

        getQueueStats: () => {
          const state = get();
          return {
            pendingCount: state.pendingSubmissions.filter(sub => sub.status === 'pending').length,
            failedCount: state.pendingSubmissions.filter(sub => sub.status === 'failed').length
          };
        }
      }),
      {
        name: 'offline-forms-storage',
        // Only persist essential data, not connection status
        partialize: (state) => ({
          cachedForms: Array.from(state.cachedForms.entries()),
          pendingSubmissions: state.pendingSubmissions,
          fileQueue: state.fileQueue,
          syncMetrics: state.syncMetrics,
          cacheMetrics: state.cacheMetrics
        }),
        // Custom serialization for Map
        onRehydrateStorage: () => (state) => {
          if (state) {
            // Convert cached forms array back to Map
            if (Array.isArray(state.cachedForms)) {
              state.cachedForms = new Map(state.cachedForms as any);
            }
          }
        }
      }
    ),
    { name: 'OfflineFormsStore' }
  )
);

// Custom hooks for specific use cases
export const useOfflineStatus = () => {
  const connection = useOfflineFormsStore(state => state.connection);
  const updateConnectionStatus = useOfflineFormsStore(state => state.updateConnectionStatus);
  
  return {
    isOnline: connection.isOnline,
    isOffline: !connection.isOnline,
    quality: connection.quality,
    lastOnlineAt: connection.lastOnlineAt,
    offlineDuration: connection.offlineDuration,
    networkType: connection.networkType,
    updateStatus: updateConnectionStatus
  };
};

export const usePendingSubmissions = () => {
  const pendingSubmissions = useOfflineFormsStore(state => state.pendingSubmissions);
  const syncStatus = useOfflineFormsStore(state => state.syncStatus);
  const syncProgress = useOfflineFormsStore(state => state.syncProgress);
  
  return {
    submissions: pendingSubmissions,
    count: pendingSubmissions.length,
    pendingCount: pendingSubmissions.filter(s => s.status === 'pending').length,
    syncingCount: pendingSubmissions.filter(s => s.status === 'syncing').length,
    failedCount: pendingSubmissions.filter(s => s.status === 'failed').length,
    conflictCount: pendingSubmissions.filter(s => s.status === 'conflict').length,
    syncStatus,
    syncProgress
  };
};

export const useFormCache = () => {
  const cachedForms = useOfflineFormsStore(state => state.cachedForms);
  const cacheMetrics = useOfflineFormsStore(state => state.cacheMetrics);
  const addCachedForm = useOfflineFormsStore(state => state.addCachedForm);
  const removeCachedForm = useOfflineFormsStore(state => state.removeCachedForm);
  const getCachedForm = useOfflineFormsStore(state => state.getCachedForm);
  const updateFormUsage = useOfflineFormsStore(state => state.updateFormUsage);
  
  return {
    cachedForms: Array.from(cachedForms.values()),
    metrics: cacheMetrics,
    isFormCached: (formId: string) => cachedForms.has(formId),
    getCachedForm,
    addForm: addCachedForm,
    removeForm: removeCachedForm,
    markFormUsed: updateFormUsage
  };
};

export const useFileUploads = () => {
  const fileQueue = useOfflineFormsStore(state => state.fileQueue);
  const uploadProgress = useOfflineFormsStore(state => state.uploadProgress);
  const addFileToQueue = useOfflineFormsStore(state => state.addFileToQueue);
  const updateFileProgress = useOfflineFormsStore(state => state.updateFileProgress);
  const updateFileStatus = useOfflineFormsStore(state => state.updateFileStatus);
  const removeFileFromQueue = useOfflineFormsStore(state => state.removeFileFromQueue);
  
  return {
    queue: fileQueue,
    progress: uploadProgress,
    queuedCount: fileQueue.filter(f => f.status === 'queued').length,
    uploadingCount: fileQueue.filter(f => f.status === 'uploading').length,
    addFile: addFileToQueue,
    updateProgress: updateFileProgress,
    updateStatus: updateFileStatus,
    removeFile: removeFileFromQueue
  };
};

export default useOfflineFormsStore;